/**
 * Menü Tanımı Taslakları — kullanıcının kendi bilgisayarında (localStorage) tutulur.
 *
 * Amaç: içeriği birbirinin aynı olan çok sayıda menüyü (ör. 50 menünün hepsinde
 * aynı ürünler) tek tek girmek zorunda kalmamak. Kullanıcı bir menünün içeriğini
 * taslak olarak kaydeder, diğer menülerde tek tıkla uygular.
 *
 * NEDEN SUNUCU DEĞİL: taslak kişisel bir kolaylık; şirket verisi değil. Sunucuya
 * yazmak için yeni tablo/uç gerekirdi. Bunun bedeli: taslaklar tarayıcıya bağlıdır
 * (başka bilgisayarda ya da tarayıcı verisi silinince görünmez).
 *
 * Anahtar şirkete göre ayrılır — aynı tarayıcıda farklı şirketlere bakan bir
 * kullanıcı başka şirketin taslaklarını görmez (bkz. n11_logs_{companyId} deseni).
 */

/** Taslaktaki ekstra opsiyon (menü ürününe bağlı fiyat farklı alternatif). */
export interface MenuTemplateExtraOption {
  optionItemId: string;
  optionItemName: string;
  optionItemUnit: string;
  priceDifference: number;
}

/** Taslaktaki normal (zorunlu) menü ürünü. */
export interface MenuTemplateItem {
  itemId: string;
  itemName: string;
  unit: string;
  quantity: number;
  wastagePercent: number;
  notes?: string;
  extraOptions?: MenuTemplateExtraOption[];
}

/** Taslaktaki opsiyon grubu — içinden yalnızca BİR ürün seçilebilir. */
export interface MenuTemplateOptionGroup {
  optionGroupName: string;
  items: Array<{
    itemId: string;
    itemName: string;
    unit: string;
    quantity: number;
    wastagePercent: number;
    isDefaultOption: boolean;
    extraOptions?: MenuTemplateExtraOption[];
  }>;
}

export interface MenuTemplate {
  id: string;
  name: string;
  createdAt: string;
  /** Taslağın alındığı ürün adı — listede "nereden kaydettim" bilgisi için. */
  sourceItemName?: string;
  items: MenuTemplateItem[];
  optionGroups: MenuTemplateOptionGroup[];
}

/** Şema değişirse eski kayıtlar sessizce bozulmasın diye sürüm anahtarda. */
const STORAGE_PREFIX = 'INVENTORY_MENU_TEMPLATES_V1';

/** Tarayıcı kotasını şişirmemek için üst sınır; en eski taslak düşer. */
const MAX_TEMPLATES = 50;

const storageKey = (companyId: string) => `${STORAGE_PREFIX}_${companyId}`;

/**
 * Şirketin taslaklarını okur. Bozuk/elle düzenlenmiş JSON'da boş liste döner —
 * taslak kritik veri değil, hata fırlatıp modalı kilitlemesine değmez.
 */
export const loadMenuTemplates = (companyId: string): MenuTemplate[] => {
  if (!companyId) return [];
  try {
    const raw = localStorage.getItem(storageKey(companyId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t: any) => t && typeof t.id === 'string' && typeof t.name === 'string'
    ) as MenuTemplate[];
  } catch {
    return [];
  }
};

/** Kota dolduğunda yazma sessizce başarısız olmasın diye sonuç döner. */
const persist = (companyId: string, templates: MenuTemplate[]): boolean => {
  try {
    localStorage.setItem(storageKey(companyId), JSON.stringify(templates));
    return true;
  } catch {
    return false;
  }
};

/**
 * Taslak ekler ya da aynı ADdaki taslağın üzerine yazar (kullanıcı aynı ismi
 * verdiğinde "güncelle" demek istiyor; liste aynı isimden iki kayıtla dolmasın).
 * Dönen liste güncel haliyle verilir ki çağıran tarafta tekrar okumaya gerek olmasın.
 */
export const saveMenuTemplate = (
  companyId: string,
  template: Omit<MenuTemplate, 'id' | 'createdAt'>
): { templates: MenuTemplate[]; saved: boolean } => {
  const existing = loadMenuTemplates(companyId);
  const name = template.name.trim();

  const record: MenuTemplate = {
    ...template,
    name,
    id: `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
  };

  const sameNameIndex = existing.findIndex(
    (t) => t.name.trim().toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr')
  );

  let next: MenuTemplate[];
  if (sameNameIndex >= 0) {
    next = [...existing];
    next[sameNameIndex] = { ...record, id: existing[sameNameIndex].id };
  } else {
    next = [record, ...existing].slice(0, MAX_TEMPLATES);
  }

  return { templates: next, saved: persist(companyId, next) };
};

export const deleteMenuTemplate = (
  companyId: string,
  templateId: string
): MenuTemplate[] => {
  const next = loadMenuTemplates(companyId).filter((t) => t.id !== templateId);
  persist(companyId, next);
  return next;
};

/** Taslaktaki toplam satır sayısı (liste ve buton etiketlerinde gösterilir). */
export const countTemplateRows = (template: MenuTemplate): number =>
  template.items.length +
  template.optionGroups.reduce((sum, g) => sum + g.items.length, 0);
